import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Spin,
  App,
  Space,
  Form,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  listCompetitionRegions,
  listAllStates,
  listCitiesByState,
  listGenders,
  listPersonalInterests,
} from '../../services/catalogs';
import { getClientsCountByDemographics } from '../../services/reports';
import { exportToExcel } from '../../utils/excel';
import debounce from 'lodash/debounce';

const AGE_RANGES = [
  { value: '13-17', label: '13-17' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45+', label: '45+' },
];

const InterestsReport = () => {
  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [form] = Form.useForm();

  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [genders, setGenders] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const stateId = Form.useWatch('stateId', form);

  useEffect(() => {
    Promise.all([
      listCompetitionRegions(),
      listAllStates(),
      listGenders(),
      listPersonalInterests(),
    ]).then(([z, s, g, i]) => {
      setZones(z || []);
      setStates(s || []);
      setGenders(g || []);
      setInterests(i || []);
    });
  }, []);

  useEffect(() => {
    if (stateId) {
      listCitiesByState(stateId).then(setCities);
    } else {
      setCities([]);
      form.setFieldValue('cityId', undefined);
    }
  }, [stateId, form]);

  const fetchCities = useMemo(
    () =>
      debounce(async (value) => {
        if (!value || !stateId) return;
        setLoadingCities(true);
        try {
          const result = await listCitiesByState(stateId, value);
          setCities(result || []);
        } finally {
          setLoadingCities(false);
        }
      }, 500),
    [stateId]
  );

  const buildParams = () => {
    const values = form.getFieldsValue();
    const params = {};
    if (values.zoneId != null) params.zoneId = values.zoneId;
    if (values.stateId != null) params.stateId = values.stateId;
    if (values.cityId != null) params.cityId = values.cityId;
    if (values.genderId != null) params.genderId = values.genderId;
    if (values.ageRange) params.ageRange = values.ageRange;
    if (values.interestId != null) params.interestId = values.interestId;
    return params;
  };

  const handleSearch = async () => {
    const params = buildParams();
    if (Object.keys(params).length < 1) {
      message.warning('Debe seleccionar al menos un filtro para consultar.');
      return;
    }
    setLoading(true);
    try {
      const res = await getClientsCountByDemographics(params);
      if (res?.ok && res?.data) {
        setData(res.data.items || []);
        setTotalCount(res.data.total ?? 0);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (err) {
      message.error(err?.__normalized?.message || 'Error al obtener el reporte.');
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setData([]);
    setTotalCount(0);
    setCities([]);
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning('No hay datos para exportar. Ejecute primero la búsqueda.');
      return;
    }
    setExporting(true);
    try {
      const excelData = data.map((item) => ({
        Zona: item.zona ?? '',
        Estado: item.estado ?? '',
        Ciudad: item.ciudad ?? '',
        Género: item.genero ?? '',
        'Rango de edad': item.edadRango ?? '',
        Interés: item.interes ?? '',
        '#': item.cantidad ?? 0,
      }));
      const columnWidths = [
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 14 },
        { wch: 14 },
        { wch: 24 },
        { wch: 8 },
      ];
      exportToExcel(
        excelData,
        columnWidths,
        'Reportes - Momento de Consumo',
        'reporte_momento_consumo'
      );
      message.success('Archivo Excel exportado correctamente.');
    } catch (err) {
      console.log(err);
      message.error('Error al exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Zona',
      dataIndex: 'zona',
      key: 'zona',
      sorter: (a, b) => (a.zona || '').localeCompare(b.zona || ''),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
    },
    {
      title: 'Ciudad',
      dataIndex: 'ciudad',
      key: 'ciudad',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.ciudad || '').localeCompare(b.ciudad || ''),
    },
    {
      title: 'Género',
      dataIndex: 'genero',
      key: 'genero',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.genero || '').localeCompare(b.genero || ''),
    },
    {
      title: 'Rango de edad',
      dataIndex: 'edadRango',
      key: 'edadRango',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.edadRango || '').localeCompare(b.edadRango || ''),
    },
    {
      title: 'Interés',
      dataIndex: 'interes',
      key: 'interes',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.interes || '').localeCompare(b.interes || ''),
    },
    {
      title: '#',
      dataIndex: 'cantidad',
      key: 'cantidad',
      sorter: (a, b) => (a.cantidad ?? 0) - (b.cantidad ?? 0),
      defaultSortOrder: 'descend',
    },
  ];

  const dataSource = data.map((item, idx) => ({ ...item, key: idx }));

  return (
    <Card
      title='Reportes - Segmentación por Momento de Consumo'
      style={{ margin: 20 }}
      extra={
        <Space>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={handleSearch}
            loading={loading}
          >
            Buscar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            Limpiar
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!data.length}
          >
            Exportar Excel
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="zoneId" label="Zona">
              <Select
                allowClear
                placeholder="Zona"
                options={zones.map((z) => ({ value: z.id, label: z.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="stateId" label="Estado">
              <Select
                allowClear
                placeholder="Estado"
                options={states.map((s) => ({ value: s.id, label: s.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="cityId" label="Ciudad">
              <Select
                allowClear
                showSearch
                filterOption={false}
                onSearch={fetchCities}
                loading={loadingCities}
                placeholder="Ciudad"
                disabled={!stateId}
                options={cities.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="genderId" label="Género">
              <Select
                allowClear
                placeholder="Género"
                options={genders.map((g) => ({ value: g.id, label: g.description }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="ageRange" label="Rango de edad">
              <Select
                allowClear
                placeholder="Rango de edad"
                options={AGE_RANGES}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="interestId" label="Intereses">
              <Select
                allowClear
                placeholder="Interés"
                options={interests.map((i) => ({ value: i.id, label: i.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
          }}
          scroll={{ x: 'max-content' }}
          summary={() =>
            totalCount > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <strong>Total general</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{totalCount}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      </Spin>
    </Card>
  );
};

export default InterestsReport;
